import React, { useState, useEffect } from 'react';
import { useLoaderData, useSearchParams } from "react-router-dom";
import LoadingScreen from './LoadingScreen';
import { createDollarFormatter } from './TableUtils.js';
import './TableUtils.css';
import './TaxReports.css';

import 'react-tabulator/lib/styles.css';
import 'react-tabulator/lib/css/tabulator.min.css';
import { ReactTabulator } from 'react-tabulator';

// Cache promises to avoid duplicate in-flight requests
let _taxYearsPromise = null;
const getTaxYears = () => {
  if (_taxYearsPromise) return _taxYearsPromise;
  const url = (process.env.NODE_ENV === 'development' ? '/tax_years.json' : '/tax_years');
  _taxYearsPromise = fetch(url).then((res) => res.json());
  return _taxYearsPromise;
};

const _capitalGainsCache = new Map();
const getCapitalGains = (year) => {
  if (_capitalGainsCache.has(year)) return _capitalGainsCache.get(year);
  const url = (process.env.NODE_ENV === 'development' ? '/capital_gains.json' : '/capital_gains?year=' + year);
  const promise = fetch(url).then((res) => res.json());
  _capitalGainsCache.set(year, promise);
  return promise;
};

const _fbarCache = new Map();
const getFbar = (year) => {
  if (_fbarCache.has(year)) return _fbarCache.get(year);
  const url = (process.env.NODE_ENV === 'development' ? '/fbar.json' : '/fbar?year=' + year);
  const promise = fetch(url).then((res) => res.json());
  _fbarCache.set(year, promise);
  return promise;
};

const DEFAULT_YEAR = String(new Date().getFullYear() - 1);

export const loader = async ({ request }) => {
  const url = new URL(request.url);
  const year = url.searchParams.get('year') || DEFAULT_YEAR;
  const tab = url.searchParams.get('tab') || 'capital_gains';

  getTaxYears();
  if (tab === 'fbar') {
    getFbar(year);
  } else {
    getCapitalGains(year);
  }

  return { initialYear: year, initialTab: tab };
};

const dollarFmt = createDollarFormatter(2);

const capitalGainsColumns = [
  { title: "Term", field: "term", minWidth: 70 },
  { title: "Description of Property", field: "desc_of_property", minWidth: 180 },
  { title: "Date Acquired", field: "date_acquired", minWidth: 110 },
  { title: "Date Disposed", field: "date_disposed", minWidth: 110 },
  { title: "Proceeds", field: "proceeds_value", formatter: dollarFmt, hozAlign: "right", minWidth: 110 },
  { title: "Cost Basis", field: "cost_basis_value", formatter: dollarFmt, hozAlign: "right", minWidth: 110 },
  { title: "Gain/Loss", field: "gain_value", formatter: dollarFmt, hozAlign: "right", minWidth: 110 },
  { title: "Note", field: "note", minWidth: 160 },
];

const fbarColumns = [
  { title: "Account", field: "account", minWidth: 200 },
  { title: "Max Value (USD)", field: "max_value_usd", formatter: dollarFmt, hozAlign: "right", minWidth: 150 },
];

function TaxReports() {
  const { initialYear, initialTab } = useLoaderData();
  const [, setSearchParams] = useSearchParams();

  const [year, setYear] = useState(initialYear);
  const [tab, setTab] = useState(initialTab);
  const [years, setYears] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Resolve available years (one-time)
  useEffect(() => {
    let cancelled = false;
    getTaxYears().then((y) => {
      if (!cancelled) {
        const strYears = y.map(String);
        setYears(strYears);
        setYear((prev) => (strYears.includes(prev) ? prev : (strYears[0] || prev)));
      }
    });
    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch data when year/tab changes
  useEffect(() => {
    if (!year) return;
    let cancelled = false;
    setLoading(true);
    const promise = tab === 'fbar' ? getFbar(year) : getCapitalGains(year);
    promise.then((result) => {
      if (!cancelled) {
        setData(result);
        setLoading(false);
      }
    });
  }, [year, tab]);

  const handleYearChange = (e) => {
    const y = e.target.value;
    setYear(y);
    const params = { year: y };
    if (tab !== 'capital_gains') params.tab = tab;
    setSearchParams(params, { replace: true });
  };

  const handleTabChange = (t) => {
    setTab(t);
    setData(null);
    const params = { year };
    if (t !== 'capital_gains') params.tab = t;
    setSearchParams(params, { replace: true });
  };

  if (!years || data === null) {
    return <LoadingScreen visible="true" />;
  }

  const columns = tab === 'fbar' ? fbarColumns : capitalGainsColumns;

  return (
    <div className="tax-reports">
      <div className="tax-reports-controls">
        <h2>Tax Reports - {year}</h2>
        <select value={year || ''} onChange={handleYearChange}>
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div className="report-toggle">
          {[['capital_gains', 'Capital Gains'], ['fbar', 'FBAR']].map(([key, label]) => (
            <button
              key={key}
              className={tab === key ? 'active' : ''}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className={'tax-reports-table-wrapper' + (loading ? ' loading' : '')}>
        <ReactTabulator
          data={data}
          columns={columns}
          options={{
            responsiveLayout: "hide",
          }}
        />
      </div>
    </div>
  );
}

export default TaxReports;
